import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import { Label } from '@/components/ui-kit/label';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { AnswerValue, Question } from '../../types/submission.types';

export interface AnswerQuestionFieldProps {
  question: Question;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  readOnly?: boolean;
}

export const AnswerQuestionField = ({
  question,
  value,
  onChange,
  readOnly,
}: AnswerQuestionFieldProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2 rounded-md border p-4">
      <Label>
        {question.Question}
        {question.IsRequired && <span className="text-destructive"> *</span>}
      </Label>
      {question.QuestionDescription && (
        <p className="text-xs text-muted-foreground">{question.QuestionDescription}</p>
      )}

      {question.FormatType === 'short-text' && (
        <Input
          disabled={readOnly}
          value={value.Answer ?? ''}
          onChange={(e) => onChange({ ...value, Answer: e.target.value })}
        />
      )}

      {question.FormatType === 'long-text' && (
        <Textarea
          rows={3}
          disabled={readOnly}
          value={value.Answer ?? ''}
          onChange={(e) => onChange({ ...value, Answer: e.target.value })}
        />
      )}

      {question.FormatType === 'date' && (
        <DatePicker
          disabled={readOnly}
          value={value.Answer ?? ''}
          onChange={(next) => onChange({ ...value, Answer: next })}
          className="w-full"
        />
      )}

      {question.FormatType === 'multiple-choice' && (
        <RadioGroup
          value={value.McOptionsValue ?? ''}
          onValueChange={(next) => onChange({ ...value, McOptionsValue: next })}
          className="flex flex-col gap-2"
        >
          {(question.Options ?? []).map((option) => (
            <label key={option.Value} className="flex min-h-9 items-center gap-2 text-sm">
              <RadioGroupItem value={option.Value} disabled={readOnly} />
              {option.Label}
            </label>
          ))}
        </RadioGroup>
      )}

      {question.FormatType === 'checkboxes' && (
        <div className="flex flex-col gap-2">
          {(question.Options ?? []).map((option) => {
            const selected = value.CbOptionsValues ?? [];
            return (
              <label key={option.Value} className="flex min-h-9 items-center gap-2 text-sm">
                <Checkbox
                  disabled={readOnly}
                  checked={selected.includes(option.Value)}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...value,
                      CbOptionsValues: checked
                        ? [...selected, option.Value]
                        : selected.filter((v) => v !== option.Value),
                    })
                  }
                />
                {option.Label}
              </label>
            );
          })}
        </div>
      )}

      {(question.FormatType === 'rating' || question.FormatType === 'nps') && (
        <Input
          type="number"
          min={question.MinRating ?? 0}
          max={question.MaxRating ?? 5}
          disabled={readOnly}
          value={value.Rating ?? ''}
          onChange={(e) => onChange({ ...value, Rating: e.target.valueAsNumber })}
        />
      )}

      {question.FormatType === 'likert-scale' && (
        <>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead />
                  {(question.LikertLabels ?? []).map((label) => (
                    <TableHead key={label}>{label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(question.LikertStatements ?? []).map((statement) => (
                  <TableRow key={statement}>
                    <TableCell>{statement}</TableCell>
                    {(question.LikertLabels ?? []).map((label) => (
                      <TableCell key={label}>
                        <input
                          type="radio"
                          disabled={readOnly}
                          name={`${question.QuestionId}-${statement}`}
                          checked={value.LikertStatementValues?.[statement] === label}
                          onChange={() =>
                            onChange({
                              ...value,
                              LikertStatementValues: {
                                ...value.LikertStatementValues,
                                [statement]: label,
                              },
                            })
                          }
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-3 md:hidden">
            {(question.LikertStatements ?? []).map((statement) => (
              <div
                key={statement}
                role="group"
                aria-label={statement}
                className="space-y-1 rounded-md border p-3"
              >
                <p className="px-1 text-sm font-medium">{statement}</p>
                {(question.LikertLabels ?? []).map((label) => (
                  <label key={label} className="flex min-h-9 items-center gap-2 text-sm">
                    <input
                      type="radio"
                      className="size-4 shrink-0"
                      disabled={readOnly}
                      name={`${question.QuestionId}-m-${statement}`}
                      checked={value.LikertStatementValues?.[statement] === label}
                      onChange={() =>
                        onChange({
                          ...value,
                          LikertStatementValues: {
                            ...value.LikertStatementValues,
                            [statement]: label,
                          },
                        })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      {question.HasComment && (
        <Textarea
          rows={2}
          disabled={readOnly}
          placeholder={t('COMMENT')}
          value={value.Comment ?? ''}
          onChange={(e) => onChange({ ...value, Comment: e.target.value })}
        />
      )}
    </div>
  );
};
